// @flow
import React, { PureComponent, Fragment } from 'react';
import { Link } from 'react-router-dom';

import ClubSearch from 'components/club/ClubSearch';
import CardThumbnail from 'components/fragments/card/CardThumbnail';
import { STATUS_LOADING, STATUS_SUCCESS } from 'constants/statusConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { Practice } from 'types/Club';
import type { BlocFaq } from 'types/Settings';
import { type Status } from 'types/Status';
import { getPublicAssets } from 'utils/assetsUtils';
import { dynamicClassName } from 'utils/dynamicClassName';
import { getMetaBySlug } from 'utils/seoUtils';
import { handleTagEvent } from 'utils/tagManagerUtils';

export type StateProps = {
  blocs_trouver_club: BlocFaq[],
  practices: Practice[],
  ligue: number,
  practicesStatus: Status
};

export type DispatchProps = {
  fetchPractices(id: number): void,
  setPractices: (practices: string[]) => void
};

type Props = StateProps & DispatchProps;

type State = {
  inputFocused: boolean,
  geolocalisation: boolean,
};

class ClubFinder extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    inputFocused: false,
    geolocalisation: false,
  };

  renderSearchedPracticies = () => {
    const { practices = [] } = this.props;

    return practices.map(({ nom }) => (
      <li key={nom}>
        <Link
          to={{ pathname: '/trouver-un-club-de-rugby/resultats-de-recherche' }}
          className="label label--rounded label--khaki"
          onClick={() => {
            handleTagEvent('recherche geolocalisee', 'filtre rugby', nom);
            this.props.setPractices([nom]);
          }}>
          {nom}
        </Link>
      </li>
    ));
  }

  renderForm = () => {
    const classNamesInput = dynamicClassName('search-club');
    this.state.inputFocused && classNamesInput.add('has-autocompletion');

    return (
      <form action="trouver-un-club-result.html" className={classNamesInput.build()}>
        <h1 className="ft-h2">Trouver un club</h1>
        <p className="banner__desc mb-5 mb-md-4">
          Retrouvez les clubs les plus proches de chez vous, filtrés par pratique, nombre de licenciés, horaires,
          compétitions et apprenez en plus sur votre futur club !
        </p>

        <ClubSearch
          ligue={this.props.ligue}
          handleInputClicked={this.handleInputClicked}
          handleClickOutside={this.handleClickOutside}
          geolocalisation={this.state.geolocalisation}
        />

        <div className="search-club__type">
          <p className="ft-h3 ft-400 mb-2">Types de rugby</p>
          <ul className="list-label mb-2">{this.renderSearchedPracticies()}</ul>
        </div>
      </form>
    );
  };

  handleInputClicked = () => {
    this.setState({ inputFocused: true });
  };

  handleClickOutside = () => {
    this.setState({ inputFocused: false });
  };

  renderCardThumbnail = () => {
    const { blocs_trouver_club } = this.props;

    return blocs_trouver_club.map((thumbnail, index) => {
      let url = '';
      if (thumbnail.parent_slugs[1]) {
        url = `${thumbnail.parent_slugs[1]}/`;
      }
      if (thumbnail.parent_slugs[2]) {
        url = `${url}${thumbnail.parent_slugs[2]}/`;
      }
      url = `${url}${thumbnail.object_slug}`;
      return (
        <div key={index} className="col col-md-4 mb-2 mb-md-0">
          <CardThumbnail
            url={`/${url}`}
            backgroundUrl={thumbnail.featured_image}
            title={thumbnail.title}
            desc={thumbnail.description}
            linkLabel={thumbnail.texte_cta} />
        </div>
      );
    });
  };

  componentDidMount() {
    const { ligue, practicesStatus } = this.props;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.setState({geolocalisation: true});
      }, (error) => {
          console.warn(`ERREUR (${error.code}): ${error.message}`);
          this.setState({geolocalisation: false});
      });
    } else {
      this.setState({geolocalisation: false});
    }

    if (
      ligue
      && practicesStatus !== STATUS_SUCCESS
      && practicesStatus !== STATUS_LOADING
    ) {
      this.props.fetchPractices(ligue);
    }
  }

  componentDidUpdate(prevProps) {
    const { ligue, practicesStatus } = this.props;

    if (
      ligue
      && ligue !== prevProps.ligue
      && practicesStatus !== STATUS_SUCCESS
      && practicesStatus !== STATUS_LOADING
    ) {
      this.props.fetchPractices(this.props.ligue);
    }
  }

  render() {
    return (
      <Fragment>
        <section className="banner banner--searchClub">
          <div
            className="banner__thumbnail img-green"
            style={{ backgroundImage: `url(${getPublicAssets('/img/banner/trouver-club.jpg')})` }}
          />
          <div className="container">
            <ol className="breadcrumb is-dark ft-h6 ft-up ">
              <li>
                <Link to="/" title="TBD">accueil</Link>
              </li>
              <li>
                <span>Trouver un club</span>
              </li>
            </ol>
            {this.renderForm()}
          </div>
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7">
            {this.renderCardThumbnail()}
          </div>
        </div>
      </Fragment>
    );
  }
}

export default withHelmet(ClubFinder)((Helmet, pageProps) => {
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title()}</title>
      <meta name={'description'} content={meta.description()} />
      <meta property={'og:title'} content={meta.title()} />
      <meta property={'og:description'} content={meta.description()} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
